import React, { useState } from "react";
import { MessageCircle, Send, FileSignature, FileText, Paperclip, Pencil, Trash2, MoreVertical, X, MapPin } from "lucide-react";

const STATUS_COLORS = {
  "Dalam Penawaran": "bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/20",
  Dijadwalkan: "bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20",
  Berjalan: "bg-[#D97706]/10 text-[#D97706] border-[#D97706]/20",
  Selesai: "bg-[#059669]/10 text-[#059669] border-[#059669]/20",
  Dibatalkan: "bg-[#DC2626]/10 text-[#DC2626] border-[#DC2626]/20",
};

const formatIDR = (n) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(Number(n) || 0);

const Item = ({ icon: Icon, label, onClick, color = "", testid }) => (
  <button onClick={onClick} data-testid={testid} className={`flex items-center gap-3 w-full px-4 py-3 text-sm text-left hover:bg-[#F4F4F5] min-h-[44px] ${color}`}>
    <Icon size={16} /> {label}
  </button>
);

const OrderMobileCard = ({ o, onStatusWa, onTechWa, onQuotWa, onQuotPdf, onInvoiceWa, onInvoicePdf, onDocs, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="border-b border-[#E4E4E7] p-4 bg-white" data-testid={`order-card-${o.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-mono text-[#52525B] uppercase tracking-wider">{o.order_no}</div>
          <div className="font-bold text-base text-[#111113] truncate mt-0.5">{o.customer?.name || "—"}</div>
          {(() => {
            const loc = (o.customer?.location_link || o.customer?.maps_link || "").trim() || (o.customer?.latitude && o.customer?.longitude ? `https://maps.google.com/?q=${o.customer.latitude},${o.customer.longitude}` : "");
            if (!loc) return null;
            return (
              <a href={loc} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[10px] text-[#002FA7] hover:underline mt-0.5" data-testid={`order-shareloc-m-${o.id}`}>
                <MapPin size={10} className="shrink-0" /> Shareloc GMaps
              </a>
            );
          })()}
          <div className="text-xs text-[#52525B] mt-1 font-mono">{o.schedule_date}</div>
        </div>
        <div className="font-mono font-bold text-base text-right whitespace-nowrap">{formatIDR(o.total)}</div>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className={`text-[10px] font-bold px-2 py-0.5 border ${STATUS_COLORS[o.work_status] || ""}`}>{o.work_status}</span>
        <span className="text-[10px] text-[#52525B]">· {o.payment_status}</span>
        {(o.jasa_labels || []).map((lbl, i) => (
          <span key={i} className="text-[9px] font-bold px-1.5 py-0.5 border bg-[#2563EB]/10 text-[#2563EB] border-[#2563EB]/20 tracking-wide uppercase" data-testid={`order-badge-jasa-m-${o.id}`}>{lbl}</span>
        ))}
      </div>

      {o.assignments?.length > 0 && (
        <div className="text-xs text-[#52525B] mb-3 truncate">
          Teknisi: {o.assignments.map(a => a.technician?.name).filter(Boolean).join(", ") || "—"}
        </div>
      )}

      {/* Primary actions (top 3) + More menu */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={onInvoiceWa} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 h-10 bg-[#002FA7] text-white text-xs font-bold" data-testid={`order-wa-invoice-m-${o.id}`}>
          <FileText size={14} /> Kirim Invoice
        </button>
        <button onClick={onEdit} className="inline-flex items-center justify-center w-10 h-10 border border-[#E4E4E7]" data-testid={`order-edit-m-${o.id}`}>
          <Pencil size={14} />
        </button>
        <button onClick={() => setMenuOpen(true)} className="inline-flex items-center justify-center w-10 h-10 border border-[#E4E4E7]" data-testid={`order-more-${o.id}`}>
          <MoreVertical size={14} />
        </button>
      </div>

      {/* Action sheet (overlay) */}
      {menuOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setMenuOpen(false)} />
          <div className="fixed bottom-0 inset-x-0 bg-white z-50 border-t border-[#E4E4E7] max-h-[80vh] overflow-y-auto" data-testid={`order-actions-sheet-${o.id}`}>
            <div className="px-4 py-3 border-b border-[#E4E4E7] flex items-center justify-between sticky top-0 bg-white">
              <div>
                <div className="label-cap text-[10px]">Aksi · {o.order_no}</div>
                <div className="font-bold text-sm truncate">{o.customer?.name}</div>
              </div>
              <button onClick={() => setMenuOpen(false)} className="p-2"><X size={18} /></button>
            </div>
            <div className="py-2">
              <div className="px-4 py-1 label-cap text-[10px]">WhatsApp</div>
              <Item icon={MessageCircle} label="Status ke Pelanggan" color="text-[#059669]" onClick={() => { setMenuOpen(false); onStatusWa(); }} testid={`order-wa-status-m-${o.id}`} />
              <Item icon={Send} label="Jadwal ke Teknisi" color="text-[#2563EB]" onClick={() => { setMenuOpen(false); onTechWa(); }} testid={`order-wa-tech-m-${o.id}`} />
              <Item icon={FileSignature} label="Penawaran via WA" color="text-[#7C3AED]" onClick={() => { setMenuOpen(false); onQuotWa(); }} testid={`order-wa-quotation-m-${o.id}`} />

              <div className="px-4 py-1 label-cap text-[10px] mt-2 border-t border-[#E4E4E7] pt-3">Dokumen</div>
              <Item icon={FileText} label="Lihat PDF Penawaran" color="text-[#7C3AED]" onClick={() => { setMenuOpen(false); onQuotPdf(); }} testid={`order-quotation-pdf-m-${o.id}`} />
              <Item icon={FileText} label="Lihat PDF Invoice" color="text-[#002FA7]" onClick={() => { setMenuOpen(false); onInvoicePdf(); }} testid={`order-pdf-m-${o.id}`} />
              <Item icon={Paperclip} label="Dokumentasi (Drive)" onClick={() => { setMenuOpen(false); onDocs(); }} testid={`order-docs-m-${o.id}`} />

              <div className="px-4 py-1 label-cap text-[10px] mt-2 border-t border-[#E4E4E7] pt-3">Lainnya</div>
              <Item icon={Trash2} label="Hapus Order" color="text-[#DC2626]" onClick={() => { setMenuOpen(false); onDelete(); }} testid={`order-delete-m-${o.id}`} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrderMobileCard;
